import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  useTheme,
  Alert,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CloseOutlined,
  Download,
  Search,
  MapOutlined,
  CheckCircle,
  SelectAll,
  DeselectOutlined,
} from "@mui/icons-material";
import { useEffect, useState, useMemo } from "react";
import api from "../../../services/api";

function MapCard({ map, selected, disabled, onToggle }) {
  const theme = useTheme();
  const isSelected = selected;

  return (
    <Box
      onClick={() => !disabled && onToggle()}
      sx={{
        position: "relative",
        borderRadius: "8px",
        border: "2px solid",
        borderColor: isSelected
          ? theme.palette.primary.main
          : theme.palette.divider,
        backgroundColor: isSelected
          ? alpha(theme.palette.primary.main, 0.07)
          : theme.palette.background.default,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.15s ease",
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        userSelect: "none",
        "&:hover": !disabled && {
          borderColor: isSelected
            ? theme.palette.primary.dark
            : theme.palette.primary.light,
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.main, 0.1)
            : alpha(theme.palette.primary.main, 0.04),
          boxShadow: "0px 2px 8px " + alpha(theme.palette.primary.main, 0.15),
        },
      }}
    >
      {isSelected && (
        <CheckCircle
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            fontSize: "18px",
            color: theme.palette.primary.main,
          }}
        />
      )}

      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        pr={isSelected ? 2.5 : 0}
      >
        <MapOutlined
          sx={{
            fontSize: "16px",
            color: isSelected
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            flexShrink: 0,
          }}
        />
        <Tooltip
          title={map.name}
          placement="top"
          disableHoverListener={map.name.length < 22}
        >
          <Typography
            fontSize="0.8rem"
            fontWeight={isSelected ? 600 : 400}
            color={isSelected ? "primary" : "text.primary"}
            noWrap
            sx={{ flex: 1, minWidth: 0 }}
          >
            {map.name}
          </Typography>
        </Tooltip>
      </Stack>

      <Typography fontSize="0.7rem" color="text.disabled" sx={{ pl: "22px" }}>
        ID {map.sysmapid}
      </Typography>
    </Box>
  );
}

export default function ModalImportZabbix({
  open,
  setOpen = () => {},
  onImportSuccess = () => {},
}) {
  const theme = useTheme();
  const [maps, setMaps] = useState([]);
  const [loadingMaps, setLoadingMaps] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState(null);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (open) {
      setSelected([]);
      setSearchText("");
      setError(null);
      setImportResult(null);
      fetchMaps();
    }
  }, [open]);

  async function fetchMaps() {
    setLoadingMaps(true);
    setError(null);
    try {
      const response = await api.get("/easyMaps/zabbix/maps");
      setMaps(response.data || []);
    } catch (err) {
      setError("Não foi possível carregar os mapas do Zabbix.");
    } finally {
      setLoadingMaps(false);
    }
  }

  const allMapsAlreadyImported = useMemo(() => {
    if (maps.length === 0) return false;
    return maps.every((m) => m.exists);
  }, [maps]);

  const filteredMaps = useMemo(
    () =>
      maps.filter((m) =>
        m.name.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [maps, searchText],
  );

  const selectableFilteredMaps = filteredMaps.filter((m) => !m.exists);

  const allFilteredSelected =
    selectableFilteredMaps.length > 0 &&
    selectableFilteredMaps.every((m) => selected.includes(m.sysmapid));

  const someFilteredSelected = selectableFilteredMaps.some((m) =>
    selected.includes(m.sysmapid),
  );

  function handleToggle(sysmapid) {
    setSelected((prev) =>
      prev.includes(sysmapid)
        ? prev.filter((id) => id !== sysmapid)
        : [...prev, sysmapid],
    );
  }

  function handleToggleAll() {
    if (allFilteredSelected) {
      const filteredIds = filteredMaps
        .filter((m) => !m.exists)
        .map((m) => m.sysmapid);
      setSelected((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      const filteredIds = filteredMaps
        .filter((m) => !m.exists)
        .map((m) => m.sysmapid);
      setSelected((prev) => [...new Set([...prev, ...filteredIds])]);
    }
  }

  async function handleImport() {
    if (selected.length === 0) return;
    setLoadingImport(true);
    setError(null);
    setImportResult(null);
    try {
      const response = await api.post("/easyMaps/zabbix/importMaps", {
        mapIds: selected,
      });
      setImportResult(response.data);
      onImportSuccess();
      handleClose();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "Erro ao importar os mapas. Tente novamente.",
      );
    } finally {
      setLoadingImport(false);
    }
  }

  function handleClose() {
    if (loadingImport) return;
    setOpen(false);
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: { xs: "92dvh", sm: "640px" },
        },
      }}
    >
      <DialogTitle>Importar Mapas do Zabbix</DialogTitle>

      {loadingImport && <LinearProgress />}

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          pt: "16px !important",
          overflow: "hidden",
        }}
      >
        <Stack direction="row" gap={1} alignItems="center">
          <TextField
            placeholder="Buscar mapa..."
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            disabled={loadingMaps || loadingImport}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.background.paperLinkedMaps,
                boxShadow: "none",
              },
            }}
          />
          {!loadingMaps && filteredMaps.length > 0 && (
            <Tooltip
              title={
                allFilteredSelected ? "Desmarcar todos" : "Selecionar todos"
              }
            >
              <Button
                onClick={handleToggleAll}
                disabled={loadingImport}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  minWidth: "fit-content",
                  px: 1.5,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                }}
                startIcon={
                  allFilteredSelected ? (
                    <DeselectOutlined fontSize="small" />
                  ) : (
                    <SelectAll fontSize="small" />
                  )
                }
              >
                {allFilteredSelected ? "Desmarcar" : "Todos"}
              </Button>
            </Tooltip>
          )}
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            pr: "4px",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: "4px",
              backgroundColor: theme.palette.divider,
            },
          }}
        >
          {loadingMaps ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              height="240px"
              gap={1.5}
            >
              <CircularProgress size={32} />
              <Typography fontSize="0.85rem" color="text.secondary">
                Carregando mapas do Zabbix...
              </Typography>
            </Stack>
          ) : filteredMaps.length === 0 ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              height="240px"
              gap={1}
            >
              <MapOutlined
                sx={{ fontSize: 40, color: theme.palette.text.disabled }}
              />
              <Typography fontSize="0.875rem" color="text.secondary">
                {maps.length === 0
                  ? "Nenhum mapa encontrado no Zabbix."
                  : "Nenhum resultado para a busca."}
              </Typography>
            </Stack>
          ) : (
            <Grid2 container spacing={1}>
              {filteredMaps.map((map) => (
                <Grid2 key={map.sysmapid} size={{ xs: 12, sm: 6 }}>
                  <MapCard
                    map={map}
                    selected={selected.includes(map.sysmapid)}
                    disabled={loadingImport || map.exists}
                    onToggle={() => handleToggle(map.sysmapid)}
                  />
                </Grid2>
              ))}
            </Grid2>
          )}
        </Box>

        <Stack direction="row" alignItems="center" minHeight="24px">
          {selected.length > 0 && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={
                selected.length === 1
                  ? "1 mapa selecionado"
                  : `${selected.length} mapas selecionados`
              }
              sx={{ fontSize: "0.75rem", height: "22px" }}
            />
          )}
        </Stack>

        {allMapsAlreadyImported && (
          <Alert severity="info">
            Todos os mapas do Zabbix já foram importados.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loadingImport}
          startIcon={<CloseOutlined />}
          variant="text"
          color="primary"
          sx={{ textTransform: "none", minWidth: "110px" }}
        >
          {importResult ? "Fechar" : "Cancelar"}
        </Button>
        {!importResult && (
          <Button
            onClick={handleImport}
            disabled={selected.length === 0 || loadingImport || loadingMaps}
            startIcon={
              loadingImport ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Download />
              )
            }
            variant="contained"
            color="primary"
            sx={{ textTransform: "none", minWidth: "130px" }}
          >
            {loadingImport ? "Importando..." : "Importar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
